import React, {useEffect} from "react";
import {useLocation} from "react-router-dom";
import {useDispatch} from "react-redux";
import {useForm} from "react-hook-form";
import moment from "moment";
import {
    CreateCancelButtons,
    DateCalendarForm,
    NumericFormatCustom,
    SelectMain,
} from "components";
import {RateTypesList} from "fragments";
import {
    FindError,
    FindLoad,
    FindSuccess,
    newDateWithoutUtc,
    usePrimaryModal,
} from "utils";
import {httpRequestsOnErrorsActions, httpRequestsOnSuccessActions, payerActions} from "store";
import {Svg} from "assets";

export const AddRate = ({item, payerId}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const {handleSubmit, control, reset, setError,clearErrors, watch} = useForm();
    const {closePrimary} = usePrimaryModal();
    const ACTION_TYPE = item ? "EDIT_PAYER_RATE" : "ADD_PAYER_RATE";
    const loading = FindLoad(ACTION_TYPE);
    const success = FindSuccess(ACTION_TYPE);
    const beckError = FindError(ACTION_TYPE);
    const info = location?.state;

    useEffect(() => {
        if (item) {
            reset({
                ...item,
                type: RateTypesList.find((type) => type.id === item.type),
            });
        }
    }, []);

    useEffect(() => {
        if (success) {
            closePrimary();
            dispatch(payerActions.getPayerRates({...info, payer: payerId,}, true));
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    useEffect(() => {
        if (beckError?.error) {
            if (beckError?.error === 'Payer rates dates overlap with an existing rate.') {
                setError('effectiveDate', {
                    message: beckError?.error
                })
            }

            if (beckError?.error === 'Only one rate can exist without an end date.') {
                setError('endDate', {
                    message: beckError?.error
                })
            }
        }
    }, [beckError]);

    useEffect(() => {
        return () => {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            clearErrors()
        }
    }, [])

    const onSubmit = (data) => {
        const params = {
            ...data,
            type: data?.type?.id,
            effectiveDate: newDateWithoutUtc(data?.effectiveDate),
        };

        data?.endDate ? params.endDate = newDateWithoutUtc(data?.endDate) : delete params.endDate;

        if (item) {
            dispatch(payerActions.editPayerRate(item?.id, params));
        } else {
            dispatch(payerActions.addPayerRate({payer: payerId, ...params}));
        }
    };

    return (
        <form className="add-rate-modal-wrapper" onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header-main">
                <h3 className="modal-header-title">{item ? "Edit" : "Add"} Rate</h3>

                <button
                    className="modal-close-btn-main"
                    type="button"
                    onClick={closePrimary}
                >
                    <Svg.CloseSvg/>
                </button>
            </div>

            <div className="modal-body-main">
                <SelectMain
                    list={RateTypesList}
                    renderLabel={(option) => option?.name}
                    name="type"
                    control={control}
                    placeholder="Select Option"
                    labelHeader="Type"
                    rules={{
                        required: "type is required",
                    }}
                />

                <div className="flex-gap-16">
                    <NumericFormatCustom
                        name="callRate"
                        label="Call Rate"
                        placeholder="$0.00"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        control={control}
                        rules={{
                            required: "Call Rate is required",
                        }}
                    />

                    <NumericFormatCustom
                        name="mileRate"
                        label="Per Mile Rate"
                        placeholder="$0.00"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        control={control}
                        rules={{
                            required: "Per Mile Rate is required",
                        }}
                    />
                </div>

                <div className="flex-gap-16">
                    <NumericFormatCustom
                        name="nightCallRate"
                        label="Night Call Rate"
                        placeholder="$0.00"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        control={control}
                        rules={{
                            required: "Night Call Rate is required",
                        }}
                    />

                    <NumericFormatCustom
                        name="cancelRate"
                        label="No Show Rate"
                        placeholder="$0.00"
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        control={control}
                        rules={{
                            required: "No Show Rate is required",
                        }}
                    />
                </div>

                <div className="flex-gap-16">
                    <DateCalendarForm
                        name="effectiveDate"
                        label="Effective Date"
                        control={control}
                        max={
                            watch("endDate")
                                ? moment(watch("endDate")).format("YYYY-MM-DD")
                                : ""
                        }
                        rules={{
                            required: watch("effectiveDate") ? null : "Effective Date is required",
                        }}
                    />

                    <DateCalendarForm
                        name="endDate"
                        label="End Date"
                        control={control}
                        min={
                            watch("effectiveDate")
                                ? moment(watch("effectiveDate")).format("YYYY-MM-DD")
                                : ""
                        }
                    />
                </div>
            </div>

            <CreateCancelButtons
                createText={item ? "Save" : "Add"}
                loading={!!loading?.length}
                onCancel={closePrimary}
            />
        </form>
    );
};
